// FFHeader.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  Login,
  Logout,
  UserAvatar,
  Purchase,
  Settings,
  UserAccess,
  ConnectReference,
  Blog,
  Close,
  Book,
  Analytics,
} from '@carbon/react/icons';
import { useNavigate, Link } from 'react-router-dom';
import {
  Header,
  HeaderName,
  HeaderMenuButton,
  HeaderGlobalBar,
  HeaderGlobalAction,
  OverflowMenu,
  OverflowMenuItem,
  SkipToContent,
  SideNav,
  SideNavItems,
  SideNavLink,
  Theme,
} from '@carbon/react';
import amplitude from './../../analytics/amplitude'; // your wrapper

const BREAK_SM = 672; // Carbon small breakpoint (≈ "sm")
const BREAK_XS = 520; // extra-tight phones

const FFHeader = ({
  isLoggedIn,
  user,
  email,
  onLogout,
  isSideNavExpanded,
  onClickSideNavExpand,
}) => {
  const navigate = useNavigate();

  const [vw, setVw] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const isSmall = vw < BREAK_SM; // under 672px
  const isXS = vw < BREAK_XS; // under 520px
  const avatarSize = isSmall ? 28 : 32;

  // Username display policy:
  // - desktop: up to 14 chars
  // - small (but not XS): up to 8 chars
  // - XS: hidden
  const usernameFull = useMemo(() => {
    if (!isLoggedIn || !email) return null;
    return email.split('@')[0];
  }, [isLoggedIn, email]);

  const usernameDisplay = useMemo(() => {
    if (!usernameFull) return null;
    if (isXS) return null; // hide at very small widths
    const limit = isSmall ? 8 : 14;
    return usernameFull.length > limit
      ? usernameFull.slice(0, limit)
      : usernameFull;
  }, [usernameFull, isSmall, isXS]);

  // ✅ Only set a stable userId; no identify() call (prevents earlier runtime error)
  useEffect(() => {
    try {
      if (isLoggedIn && email && amplitude?.setUserId) {
        amplitude.setUserId(email);
      }
    } catch {
      // ignore analytics issues
    }
  }, [isLoggedIn, email]);

  const safeTrack = (name, props) => {
    try {
      amplitude?.track?.(name, props);
    } catch {
      // ignore analytics issues
    }
  };

  const handleLoginClick = () => {
    navigate('/login');
  };

  const trackAndOpenDocs = source => {
    safeTrack('Open Docs', {
      source, // 'header' | 'side-nav'
      component: 'FFHeader',
      href: '/docs/index.html',
      path: typeof window !== 'undefined' ? window.location.pathname : '',
      is_logged_in: !!isLoggedIn,
      user_id: email || user?.id || undefined,
    });
    setTimeout(() => window.open('/docs/index.html', '_blank', 'noopener'), 0);
  };

  return (
    <Theme theme="g100">
      <Header aria-label="Application Header">
        <SkipToContent />

        {/* Always display the HeaderMenuButton */}
        <HeaderMenuButton
          className="always-visible-menu-button"
          aria-label="Click to setup integrations"
          onClick={onClickSideNavExpand}
          isActive={isSideNavExpanded}
          renderMenuIcon={<ConnectReference />}
          renderCloseIcon={<Close />}
        />

        {/* AI Posts */}
        <HeaderGlobalAction
          aria-label="AI Posts"
          tooltipAlignment="end"
          tooltipPosition="left"
          onClick={() => {
            try {
              amplitude.track('Open AI Posts', { source: 'header' });
            } catch {}
            navigate('/posts');
          }}>
          <Blog size={20} />
        </HeaderGlobalAction>

        {/* Brand */}
        <HeaderName as={Link} to="/" prefix="">
          {isSmall ? 'FFAI' : 'Fresh Focus AI'}
        </HeaderName>

        {/* Right-side actions */}
        <HeaderGlobalBar className="ff-header-actions">
          {/* Docs */}
          <HeaderGlobalAction
            aria-label="Docs"
            tooltipAlignment="end"
            tooltipPosition="left"
            onClick={() => trackAndOpenDocs('header')}>
            <Book size={20} />
          </HeaderGlobalAction>

          {/* Auth area */}
          {isLoggedIn ? (
            <>
              {/* Username (hidden on XS) */}
              {usernameDisplay && (
                <div
                  className="username-text"
                  title={usernameFull}
                  aria-label={`Signed in as ${usernameFull}`}>
                  {usernameDisplay}
                </div>
              )}

              {/* Avatar + overflow menu */}
              <HeaderGlobalAction
                aria-label={`Account ${email ? `(${email})` : ''}`}
                tooltipAlignment="end"
                tooltipPosition="left"
                className="user-avatar-action">
                <OverflowMenu
                  aria-label="User Profile Menu"
                  renderIcon={() => <UserAvatar size={avatarSize} />}
                  direction="bottom"
                  flipped
                  className="username-overflow-menu">
                  <OverflowMenuItem
                    itemText={
                      <>
                        <Purchase size={20} /> &nbsp;&nbsp;Membership
                      </>
                    }
                    onClick={() => {
                      navigate('/billing');
                    }}
                  />
                  <OverflowMenuItem
                    itemText={
                      <>
                        <Analytics size={20} /> &nbsp;&nbsp;Usage
                      </>
                    }
                    onClick={() => {
                      navigate('/usage');
                    }}
                  />
                  <OverflowMenuItem
                    itemText={
                      <>
                        <Settings size={20} /> &nbsp;&nbsp;Settings
                      </>
                    }
                    onClick={() => {
                      navigate('/settings');
                    }}
                  />
                  <OverflowMenuItem
                    itemText={
                      <>
                        <UserAccess size={20} /> &nbsp;&nbsp;Privacy
                      </>
                    }
                    onClick={() => {
                      navigate('/privacy');
                    }}
                  />
                  <OverflowMenuItem
                    itemText={
                      <>
                        <Logout size={20} /> &nbsp;&nbsp;Logout
                      </>
                    }
                    onClick={onLogout}
                  />
                </OverflowMenu>
              </HeaderGlobalAction>
            </>
          ) : (
            <HeaderGlobalAction
              id="login-link"
              aria-label="Log in"
              onClick={handleLoginClick}
              tooltipAlignment="end">
              {/* icon-only on small screens to save space */}
              {isSmall ? (
                <Login size={20} />
              ) : (
                <>
                  <Login size={20} /> &nbsp; Log in
                </>
              )}
            </HeaderGlobalAction>
          )}
        </HeaderGlobalBar>
      </Header>

      {/* Side Navigation */}
      <SideNav
        aria-label="Side navigation"
        expanded={isSideNavExpanded}
        isPersistent={false}
        isFixedNav={false}
        isRail={false}
        onOverlayClick={onClickSideNavExpand}>
        <SideNavItems>
          <SideNavLink
            as={Link}
            to="/integration/email"
            onClick={onClickSideNavExpand}>
            Email Integration
          </SideNavLink>
          <SideNavLink
            as={Link}
            to="/integration/evernote"
            onClick={onClickSideNavExpand}>
            Evernote Integration
          </SideNavLink>
          <SideNavLink
            as={Link}
            to="/integration/github"
            onClick={onClickSideNavExpand}>
            GitHub Integration
          </SideNavLink>
          <SideNavLink
            as={Link}
            to="/integration/google-drive"
            onClick={onClickSideNavExpand}>
            Google Drive Integration
          </SideNavLink>
          <SideNavLink
            as={Link}
            to="/integration/s3"
            onClick={onClickSideNavExpand}>
            S3 Storage Integration
          </SideNavLink>
          <SideNavLink
            as={Link}
            to="/integration/sms"
            onClick={onClickSideNavExpand}>
            Text Message (SMS) Integration
          </SideNavLink>
          {/* TODO: X/Twitter nav link temporarily hidden — Twitter API changed, pending reimplementation */}
          <SideNavLink
            as={Link}
            to="/integration/openclaw"
            onClick={onClickSideNavExpand}>
            OpenClaw / NemoClaw
          </SideNavLink>
          <SideNavLink
            as={Link}
            to="/integration/a2a"
            onClick={onClickSideNavExpand}>
            Agent-to-Agent (A2A)
          </SideNavLink>
          <SideNavLink
            as={Link}
            to="/integration/custom-api"
            onClick={onClickSideNavExpand}>
            MCP Integrations
          </SideNavLink>

          {/* Docs link with explicit tracking */}
          <SideNavLink
            href="/docs/index.html"
            target="_blank"
            rel="noopener"
            onClick={e => {
              e.preventDefault(); // avoid double open; we open after track
              trackAndOpenDocs('side-nav');
              onClickSideNavExpand();
            }}>
            Documentation
          </SideNavLink>
        </SideNavItems>
      </SideNav>
    </Theme>
  );
};

export default FFHeader;
